<template>
  <div class="mc-delivery-request">
    <McTable
      v-show="isTableDataAvailable"
      ref="mcTable"
      stickyHeader
      withCard
      searchable
      hasCheckbox
      :fields="fields"
      :apiClient="apiClient"
      :hasSelectAll="showSelectAll"
      :activeFilters="activeFilters"
      :customHandlers="customHandlers"
      @available-change="handleDataAvailable"
      @handleSelectValue="handleSelectValue"
      @listRes="setList"
    >
      <template #before-table="{}">
        <div class="d-flex justify-content-between mb-3">
          <McInlineFilter
            :filterItems="inlineFilterItems"
            :activeKeys="activeFilters['status.name']"
            @update:activeKeys="handleChangeActiveFilters([{ key: 'status.name', values: $event }])"
          />

          <ActionsDropdown :selectedIds="selectedIds" @refresh-data="handleRefreshData" />
        </div>
      </template>

      <template #cell(status.name)="{ value, item }">
        <template>
          <span
            v-if="item.status?.color"
            class="__status-dots"
            :style="{ backgroundColor: item.status.color }"
          ></span>
          <span class="mc-text-status">{{ statusKeys[value] }}</span>
        </template>
      </template>
      <template #cell(fleet_task.number)="{ item }">
        <a v-if="item?.fleet_task?.number" @click="onRowClicked(item)">
          <strong class="text-primary">
            {{ item.fleet_task.number }}
          </strong>
        </a>
        <span v-else> - </span>
      </template>
    </McTable>
    <b-card v-if="!isTableDataAvailable && !hasFilters" class="mt-3">
      <div class="empty-table-container">
        <McEmptyState
          :title="$t('deliveryRequest.fleetTaskList.emptyTitle')"
          class="pb-4"
          placementType="list"
          :hasButton="false"
          size="lg"
        />
      </div>
    </b-card>
  </div>
</template>

<script>
import appConfig from '@/app.config'

import McInlineFilter from '@/components/McInlineFilter'
import McTable from '@/components/McTable'
import ActionsDropdown from '../components/fleet-task/ActionsDropdown.vue'
import McEmptyState from '@/components/McEmptyState'

import * as Request from '@/services/rest-api/lib/request/fleet-task'

import fields from '../lib/fields/list-table-fields-fleet-task'
import inlineFilterItems from '../lib/constants/inline-filter-items'
import statusKeys from '../lib/constants/status-keys'
import filterMappings from '../lib/constants/filter-mappings'
import { formatIndexDate } from '@/helpers/tzDate'

export default {
  page: {
    title: 'Fleet Task Request',
    meta: [{ name: 'description', content: appConfig.description }],
  },
  components: {
    McInlineFilter,
    McTable,
    ActionsDropdown,
    McEmptyState,
  },
  data() {
    return {
      activeFilters: {},
      showSelectAll: false,
      isTableDataAvailable: true,
      selectedIds: [],
      items: [],
    }
  },
  computed: {
    fields() {
      return fields(this.$t.bind(this))
    },
    inlineFilterItems() {
      return inlineFilterItems(this.$t.bind(this))
    },
    statusKeys() {
      return statusKeys(this.$t.bind(this))
    },
    apiClient() {
      let getRequest = Request.get()

      if (Object.keys(this.activeFilters).length > 0) {
        const queryParamFilters = []

        // mapping the activeFilters to be sent as query params
        Object.keys(this.activeFilters).forEach((key) => {
          const filter = filterMappings[key]?.(key, this.activeFilters[key]) ?? undefined
          queryParamFilters.push(filter)
        })

        getRequest = Request.get(queryParamFilters)
      }

      return {
        get: getRequest,
      }
    },
    customHandlers() {
      return {
        handleClickRow: this.handleClickRow,
      }
    },
  },
  methods: {
    formatIndexDate,
    handleClickRow(item) {
      const name = 'Fleet Task Request Detail'
      const id = item.id
      this.$router.push({ name, params: { id } })
    },
    handleChangeActiveFilters(filters) {
      this.showSelectAll = false
      filters.forEach((filter) => {
        // to reset the filter values
        this.$delete(this.activeFilters, filter.key)

        // to prevent set the filter in activeFilters if values are null or undefined
        if (filter.values === null || filter.values === undefined || filter.values === '') return
        // to prevent set the filter in activeFilters if values are Array with length equal to 0
        if (Array.isArray(filter.values) && filter.values.length === 0) return

        // set the key and values in activeFilters
        this.$set(this.activeFilters, filter.key, filter.values)

        if (
          filter.key === 'status.name' &&
          Array.isArray(filter.values) &&
          filter.values.length > 0
        ) {
          this.showSelectAll = true
        }
      })
    },
    handleDataAvailable(isAvailable) {
      this.isTableDataAvailable = isAvailable
    },
    handleSelectValue(ids) {
      const findSelectedIds = Object.keys(ids)
        .filter((key) => ids[key] === true)
        .map((id) => id)

      if (findSelectedIds.length === 0) {
        this.selectedIds = []
        return
      }

      findSelectedIds.forEach((id) => {
        const existingIndex = this.selectedIds.findIndex((item) => item.id === id)
        if (existingIndex === -1) {
          const item = this.items.find((item) => item.id === id)
          let status = item?.status?.name || null

          if (!status && this.activeFilters['status.name']?.includes('WAITING')) {
            status = 'Menunggu'
          }

          this.selectedIds.push({
            id: id,
            status: status,
          })
        }
      })

      this.selectedIds = this.selectedIds.filter((selected) =>
        findSelectedIds.includes(selected.id)
      )
    },

    setList(res) {
      this.items = res.data.paginated_result

      if (this.selectedIds.length > 0) {
        this.selectedIds = this.selectedIds.map((selected) => {
          const updatedItem = this.items.find((item) => item.id === selected.id)
          return {
            id: selected.id,
            status: updatedItem?.status?.name || selected.status,
          }
        })
      }
    },

    onRowClicked(item) {
      this.$router.push({
        name: 'Fleet Task Detail',
        params: {
          id: item.fleet_task.id,
        },
      })
    },

    handleRefreshData() {
      this.selectedIds = []
      this.activeFilters = { ...this.activeFilters }
      this.$refs.mcTable.ResetSelects()
    }
  },
}
</script>

<style lang="scss" scoped>
.mc-delivery-request {
  .__status-dots {
    display: inline-block;
    margin: auto;
    width: 6px;
    height: 6px;
    border-radius: 50%;
    margin-right: 8px;
  }

  .mc-text-status {
    font-weight: 600;
  }

  &__delivery-number {
    cursor: pointer;
  }
}
</style>
